<script lang="ts">
  import EntityLink from '@components/entity-link.svelte'
  import Icon from '@components/icon.svelte'
  import Image from '@components/image.svelte'
  import Panel from '@components/panel.svelte'
  import { ratingColor } from '@lib/fc'
  import { session } from '@lib/stores'
  import type {
    Colors,
    GameraGrid,
    GameraGridColumn,
    GameraGridRow,
  } from '@statmuse/core/gamera/index'
  import { orderBy, some } from 'lodash-es'
  import type { ComponentProps } from 'svelte'

  type PanelProps = ComponentProps<typeof Panel>

  const freeRowLimit = 25

  type Column = GameraGridColumn & {
    hasImage?: boolean
    sticky?: boolean
  }
  type RowItem = GameraGridRow[string]

  interface Props {
    data: GameraGrid | GameraGrid[]
    stickyColumns?: string[]
    limitRows?: boolean
    fullWidth?: boolean
    columnStyles?: object
    padding?: string
    head?: boolean
    highlight?: Record<string, Colors | undefined>
    title?: PanelProps['title']
    href?: PanelProps['href']
    entity?: PanelProps['entity']
    className?: string
    textInherit?: boolean
    rankColumn?: boolean
    disableSort?: boolean
    onRowClick?: (row: GameraGridRow) => (e: Event) => void
    rowKeyFn?: (row: GameraGridRow) => string
  }

  let {
    data,
    stickyColumns = [],
    limitRows = false,
    fullWidth = true,
    columnStyles = {},
    padding = 'px-2',
    head = true,
    highlight,
    title,
    href,
    entity,
    className,
    textInherit = false,
    rankColumn = false,
    disableSort = false,
    onRowClick,
    rowKeyFn,
  }: Props = $props()

  const styles = Object.assign(
    { ALIGNMENT: 'w-2', SEASON: 'text-center' },
    columnStyles,
  ) as Record<string, string>

  let sortKey = $state('')
  let sortOrder = $state<'asc' | 'desc'>()
  let expand = $state(false)

  const textAlign = (col: Column) => {
    switch (col.type) {
      case 'date':
      case 'number':
      case 'time':
        return 'text-right'
      case 'string':
        return 'text-left'
      default:
        return 'text-center'
    }
  }

  const applyStyles = (col: Column) => {
    const style =
      styles[col.rowItemKey] ||
      styles[col.rowItemKey?.toUpperCase()] ||
      styles[col.rowItemKey?.toLowerCase()] ||
      styles['default']
    if (/text-(left|center|right)/.test(style)) {
      return style
    }
    return `${textAlign(col)} ${style ?? ''}`
  }

  const imgClass = ({ imageUrl = '', entity }: RowItem) => {
    if (entity?.type.includes('player')) {
      return [
        'w-10',
        'h-8',
        'max-w-none',
        'object-cover',
        'object-bottom',
        'bg-gray-8 dark:bg-gray-3',
        '-mb-[1px]',
        'mt-2',
        // 'mx-auto',
        'overflow-visible',
        imageUrl.includes('silhouette') ? 'opacity-50' : '',
      ]
        .filter((x) => x)
        .join(' ')
    }
    return 'w-4 h-4 max-w-none object-contain'
  }

  const onClickSort = (key: string) => () => {
    sortOrder = sortKey === key && sortOrder === 'desc' ? 'asc' : 'desc'
    sortKey = key
  }

  const onClickExpand = (e: Event) => {
    e.preventDefault()
    expand = !expand
  }

  const dateRegex = /(Sun|Mon|Tue|Wed|Thu|Fri|Sat)\s(\d+\/\d+)/
  const scoreRegex = /(W|L|T|D)\s(\d+-\d+(\s\(\d+-\d+\sPen\))?)/

  const rankingColor = (tags?: { rankingOutOf?: number }, display?: string) => {
    if (tags?.rankingOutOf && display) {
      const match = display.match(/(\d+).*/)
      if (match) {
        const [topRank, bottomRank] = [
          // We are taking the top 25% and the bottom 30% and then rounding the result to the nearest 5
          // to get an "even" number of highlighted ranks on each end.
          Math.round((tags.rankingOutOf * 0.25) / 5) * 5,
          Math.round((tags.rankingOutOf * 0.7) / 5) * 5,
        ]
        const rank = Number(match[1])
        if (rank <= topRank) return 'text-[#009444]'
        if (rank >= bottomRank) return 'text-[#BF1D2D]'
      }
    }
    return ''
  }

  const shouldUseTeamColors = (col: GameraGridColumn) => {
    return (
      sortKey === col.rowItemKey ||
      (!sortKey && col.tags?.isReferencedInQuestion)
    )
  }

  const getRatingClasses = (
    column: GameraGridColumn,
    hasHighestValue?: boolean,
    display?: string,
  ): string | undefined => {
    if (column.rowItemKey === 'RTG') {
      const color =
        display && !shouldUseTeamColors(column)
          ? ratingColor(display, hasHighestValue)
          : undefined
      const colorClasses = color ? `rounded-full bg-${color}` : ''

      return `inline-flex justify-center min-w-10 ${colorClasses}`
    }

    return undefined
  }

  const shouldApplyRowHighlight = (
    row?: RowItem,
    highlight?: Record<string, Colors | undefined>,
  ) => {
    if (highlight && highlight[row?.value as string]) {
      return highlight[row?.value as string]
    }
    return undefined
  }

  let gridsFullData = $derived.by(() =>
    (Array.isArray(data) ? data : [data]).map((grid) => {
      let columns = grid.columns.flatMap((col) => {
        const isSticky = stickyColumns.includes(col.rowItemKey)
        const hasImage = some(grid.rows, `${col.rowItemKey}.imageUrl`)

        if (isSticky && hasImage) {
          return [
            { rowItemKey: 'IMAGE', title: '', type: 'string' },
            { ...col, sticky: true },
          ]
        }

        if (isSticky) {
          return { ...col, sticky: true }
        }

        if (hasImage) {
          return { ...col, hasImage: true }
        }

        return col
      }) as Column[]

      if (!some(columns, 'sticky')) {
        const [firstColumn, ...rest] = columns
        columns = [{ ...firstColumn, sticky: true }, ...rest]
      }

      const allRows = grid.rows.map((row) =>
        stickyColumns.reduce((r, key) => {
          if (r[key]?.imageUrl) {
            return {
              ...r,
              [key]: {
                ...r[key],
                imageUrl: undefined,
              },
              IMAGE: r[key],
            }
          }
          return r
        }, row),
      )

      let rows = limitRows ? allRows.slice(0, freeRowLimit) : allRows

      return {
        ...grid,
        allRows,
        columns,
        rows,
      }
    }),
  )

  let gridsLimitedRows = $derived.by(() => {
    if (limitRows) {
      if (
        $session?.type === 'user' &&
        $session.properties.subscriptionStatus === 'active'
      ) {
        return gridsFullData.map((grid) => ({
          ...grid,
          rows: expand ? grid.allRows : grid.allRows.slice(0, freeRowLimit),
        }))
      }
    }
    return gridsFullData
  })

  let grids = $derived.by(() => {
    if (sortKey && sortOrder) {
      const order = sortOrder
      return gridsLimitedRows.map((grid) => ({
        ...grid,
        rows: orderBy(
          grid.rows,
          [`${sortKey}.value`, `DATE.value`, `SEASON.value`, `NAME.value`],
          [order, 'desc', 'desc', 'asc'],
        ),
      }))
    }
    return gridsLimitedRows
  })
</script>

<Panel {title} {href} {entity} class={`${className} pb-0`}>
  <div class="@container/table relative overflow-x-auto -mx-3">
    <table class="whitespace-nowrap" class:w-full={fullWidth}>
      {#each grids as grid (grid.name ?? grid)}
        {@const { columns, rows, aggregations } = grid}
        {#if head}
          <thead>
            <tr class="text-sm">
              {#if rankColumn}
                <th class="pl-3"></th>
              {/if}
              {#each columns as col, index (col.rowItemKey)}
                <th
                  class={`font-normal p-1.5 text-gray-5 ${applyStyles(col)}`}
                  class:cursor-pointer={!disableSort}
                  class:pl-8={col.hasImage}
                  class:pl-3={index === 0 && !rankColumn}
                  class:pr-3={index === columns.length - 1}
                  class:sticky={col.sticky}
                  class:left-0={col.sticky}
                  class:bg-gray-8={col.sticky}
                  class:dark:bg-gray-3={col.sticky}
                  class:!bg-team-primary={shouldUseTeamColors(col)}
                  class:text-team-secondary={shouldUseTeamColors(col)}
                  onclick={!disableSort
                    ? onClickSort(col.rowItemKey)
                    : undefined}
                >
                  {col.title}
                </th>
              {/each}
            </tr>
          </thead>
        {/if}
        <tbody class="divide-y divide-gray-6 dark:divide-gray-4 leading-[22px]">
          {#each rows as row, rowIndex (rowKeyFn ? rowKeyFn(row) : row)}
            {@const highlightColors = shouldApplyRowHighlight(
              row[columns[0].rowItemKey],
              highlight,
            )}
            <tr
              class:hover:cursor-pointer={!!onRowClick}
              onclick={onRowClick ? onRowClick(row) : undefined}
            >
              {#if rankColumn}
                <td
                  class="w-2 pl-3 text-gray-5"
                  style:background={highlightColors?.backgroundColor}
                  style:color={highlightColors?.foregroundColor}
                >
                  {rowIndex + 1}
                </td>
              {/if}
              {#each columns as col, index (col.rowItemKey)}
                {#if row[col.rowItemKey]}
                  {@const { display, imageUrl, entity, meta, tags } =
                    row[col.rowItemKey]}
                  {@const ratingClasses = getRatingClasses(
                    col,
                    tags?.hasHighestValue,
                    display,
                  )}
                  <td
                    class={`${applyStyles(col)} ${padding} ${
                      imageUrl ? 'w-2' : ''
                    } ${rankingColor(tags, display)}`}
                    class:py-2.5={!imageUrl}
                    class:pl-3={index === 0 && !rankColumn}
                    class:pr-3={index === columns.length - 1}
                    class:sticky={col.sticky}
                    class:left-0={col.sticky}
                    class:bg-gray-8={col.sticky &&
                      !(sortKey === col.rowItemKey)}
                    class:dark:bg-gray-3={col.sticky &&
                      !(sortKey === col.rowItemKey)}
                    class:bg-team-primary={shouldUseTeamColors(col)}
                    class:text-team-secondary={shouldUseTeamColors(col)}
                    class:text-gray-5={col.rowItemKey === 'RANK'}
                    style:background={highlightColors?.backgroundColor}
                    style:color={highlightColors?.foregroundColor}
                  >
                    <EntityLink
                      {entity}
                      class={sortKey === col.rowItemKey ||
                      (!sortKey && col.tags?.isReferencedInQuestion)
                        ? 'text-team-secondary'
                        : textInherit
                          ? 'text-inherit'
                          : ''}
                    >
                      {#if col.rowItemKey === 'IMAGE'}
                        {#if imageUrl}
                          <Image
                            src={imageUrl}
                            width={40}
                            height={40}
                            alt={display}
                            class={imgClass(row[col.rowItemKey])}
                            loading="lazy"
                          />
                        {/if}
                      {:else if col.hasImage}
                        <div class="flex items-center">
                          {#if imageUrl}
                            <Image
                              src={imageUrl}
                              width={16}
                              height={16}
                              alt={display}
                              class={`${imgClass(row[col.rowItemKey])} mr-2.5`}
                              loading="lazy"
                            />
                          {:else}
                            <div class="w-4 h-4 mr-2.5"></div>
                          {/if}
                          {display}
                        </div>
                      {:else if dateRegex.test(display)}
                        {@const [, day, date] = Array.from(
                          display.match(dateRegex) || [],
                        )}
                        <div class="flex">
                          <div class="w-8">{day}</div>
                          <div>{date}</div>
                        </div>
                      {:else if scoreRegex.test(display)}
                        {@const [, result, score] = Array.from(
                          display.match(scoreRegex) || [],
                        )}
                        <div class="flex">
                          <div class="w-5">{result}</div>
                          <div>{score}</div>
                        </div>
                      {:else}
                        <div
                          class={meta?.position ||
                          meta?.injured ||
                          ratingClasses
                            ? 'flex items-center'
                            : ''}
                          class:pl-2={meta?.padding}
                        >
                          <span
                            class={(entity?.shortDisplay ||
                              meta?.shortDisplay) &&
                            (col.rowItemKey === 'NAME' ||
                              col.rowItemKey === 'PLAYER')
                              ? '@lg/table:block hidden'
                              : (ratingClasses ?? '')}
                          >
                            {display}
                          </span>
                          {#if (entity?.shortDisplay || meta?.shortDisplay) && (col.rowItemKey === 'NAME' || col.rowItemKey === 'PLAYER')}
                            <span class="@lg/table:hidden">
                              {entity?.shortDisplay ?? meta?.shortDisplay}
                            </span>
                          {/if}
                          {#if meta?.position}
                            <span class="text-gray-5 text-sm ml-1">
                              {meta.position}
                            </span>
                          {/if}
                          {#if meta?.injured}
                            <Icon name="injury" class="w-4 ml-1" />
                          {/if}
                        </div>
                      {/if}
                    </EntityLink>
                  </td>
                {:else}
                  <td
                    style:background={highlightColors?.backgroundColor}
                    style:color={highlightColors?.foregroundColor}
                  ></td>
                {/if}
              {/each}
            </tr>
          {/each}
          {#if aggregations}
            {#each aggregations as row (rowKeyFn ? rowKeyFn(row) : row)}
              <tr>
                {#if rankColumn}
                  <td class="w-2 pl-3 text-gray-5"></td>
                {/if}
                {#each columns as col, index (col.rowItemKey)}
                  {#if row[col.rowItemKey]}
                    {@const { display, tags } = row[col.rowItemKey]}
                    {@const ratingClasses = getRatingClasses(
                      col,
                      tags?.hasHighestValue,
                      display,
                    )}
                    <td
                      class={`${applyStyles(col)} ${padding} py-2.5`}
                      class:sticky={col.sticky}
                      class:left-0={col.sticky}
                      class:pl-3={index === 0 && !rankColumn}
                      class:pr-3={index === columns.length - 1}
                      class:bg-gray-8={col.sticky}
                      class:dark:bg-gray-3={col.sticky}
                    >
                      {#if ratingClasses}
                        <span class={ratingClasses}>
                          {display}
                        </span>
                      {:else}
                        {display}
                      {/if}
                    </td>
                  {:else}
                    <td></td>
                  {/if}
                {/each}
              </tr>
            {/each}
          {/if}
        </tbody>
        {#if limitRows && grids[0].allRows.length > freeRowLimit && ($session?.type === 'visitor' || $session?.properties.subscriptionStatus !== 'active')}
          <tbody>
            {#each Array(3) as _row}
              <tr class="border-t border-[#c7c8ca]/30">
                {#if rankColumn}
                  <td class="w-2 pl-3 text-gray-5">
                    <div class="flex items-center h-[22px]">
                      <div class="bg-[#c7c8ca]/30 w-full h-5 rounded-md"></div>
                    </div>
                  </td>
                {/if}
                {#each columns as col, index}
                  <td
                    class={`${textAlign(col)} ${padding} py-2.5`}
                    class:sticky={col.sticky}
                    class:pl-3={index === 0 && !rankColumn}
                    class:pr-3={index === columns.length - 1}
                    class:left-0={col.sticky}
                    class:bg-gray-8={col.sticky}
                    class:dark:bg-gray-3={col.sticky}
                  >
                    {#if col.rowItemKey === 'IMAGE'}
                      <div class="flex items-center justify-center h-[22px]">
                        <div class="bg-[#c7c8ca]/30 h-5 w-5 rounded-full"></div>
                      </div>
                    {:else if col.hasImage}
                      <div class="flex items-center gap-2.5 h-[22px]">
                        <div class="bg-[#c7c8ca]/30 h-4 w-4 rounded-full"></div>
                        <div
                          class="bg-[#c7c8ca]/30 h-5 flex-1 rounded-md"
                        ></div>
                      </div>
                    {:else}
                      <div class="flex items-center h-[22px]">
                        <div
                          class="bg-[#c7c8ca]/30 w-full h-5 rounded-md"
                        ></div>
                      </div>
                    {/if}
                  </td>
                {/each}
              </tr>
            {/each}
          </tbody>
        {/if}
      {/each}
    </table>
  </div>
  {#if limitRows && grids[0].allRows.length > freeRowLimit && ($session?.type === 'visitor' || $session?.properties.subscriptionStatus !== 'active')}
    <div class="absolute bottom-5 left-1/2 -translate-x-1/2">
      <a
        href="/auth/signup"
        class="flex gap-2 items-center w-fit py-3 px-4 bg-primary text-white text-xl rounded-2xl hover:no-underline"
      >
        <span class="shrink-0 mt-[1px]">Unlock 2x data</span>
        <svg
          enable-background="new 0 0 126.2 70.9"
          viewBox="0 0 126.2 70.9"
          xmlns="http://www.w3.org/2000/svg"
          class="w-10"
        >
          <path d="m51.3 1.7h15.1v67.4h-15.1z" fill="#fff"></path>
          <path
            d="m95.6 49.2c3.1-1 6-2.7 8-5.3-3.1.8-5.9 1.4-8.7 1.5-1.8.1-3.5.1-5.3-.1v-11-8.8c0-9.9-8-17.9-17.9-17.9v35.4c3.1 0 5.7 2.5 5.7 5.7v8.2c0 6.8 5.5 12.3 12.3 12.3h8.9v-8.9h-3.4c-3 0-5.5-2.5-5.5-5.5v-4.6c2-.1 4-.4 5.9-1zm-16.8-20c-1.4-1.4-1.4-3.8 0-5.2 0 1 .6 2.4 1.8 3.5 1.1 1.1 2.5 1.7 3.5 1.8-1.5 1.4-3.9 1.4-5.3-.1z"
            fill="#fff"
          ></path>
          <path d="m30.9 17.2h15.1v51.9h-15.1z" fill="#fff"></path>
          <path
            d="m10.5 28.3v20.2c0 1.8-1.1 4-3 5.9s-4.1 3-5.9 3c2.5 2.4 6.4 2.4 8.9 0v11.8h15.1v-56c-8.3 0-15.1 6.7-15.1 15.1z"
            fill="#fff"
          ></path>
          <path
            d="m113 36.2h-7.6v-28.5c4.2 0 7.6 3.4 7.6 7.6z"
            fill="#fff"
          ></path>
          <path
            d="m105.4 7.7h7.6v28.6h-7.6z"
            transform="matrix(0 -1 1 0 87.2674 131.158)"
            fill="#fff"
          ></path>
        </svg>
      </a>
    </div>
  {/if}
  {#if limitRows && grids[0].allRows.length > freeRowLimit && $session?.type === 'user' && $session.properties.subscriptionStatus === 'active'}
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      class="border-t border-gray-6 dark:border-gray-4 py-1 mt-0.5 -mx-3"
      onclick={onClickExpand}
    >
      <!-- svelte-ignore a11y_invalid_attribute -->
      <a href="#" class="text-primary flex items-center justify-center gap-2">
        Show {expand ? 'less' : 'more'}
        <Icon name="dropdown" class={`w-4 h-4 ${expand ? 'rotate-180' : ''}`} />
      </a>
    </div>
  {/if}
</Panel>
